import loadGoogleMapsApi from "load-google-maps-api";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import {
    IKilometers,
    IMiles,
    Latitude,
    Longitude,
    Meters,
    isoKilometers,
    isoLatitude,
    isoLongitude,
    isoMeters,
    isoMiles,
} from "../models/nominals";
import { getPageSetting } from "./settings";

const COORD_MAX_DECIMALS = 6;
const METERS_PER_MILE = 1_609.344;
const METERS_PER_KILOMETER = 1_000;

let __loadingGMaps: ReturnType<typeof loadGoogleMapsApi> | undefined;
let numMaps = 0;

export const loadGMaps = (): Promise<typeof google.maps> => {
    if (!__loadingGMaps) {
        const apiKey = getPageSetting("google_maps_api_key");
        if (!apiKey) {
            throw new Error("Google Maps API key is not configured");
        }
        __loadingGMaps = loadGoogleMapsApi({
            key: apiKey,
            libraries: ["places"],
        }).catch((err) => {
            // Reset the cached promise so subsequent calls can retry
            __loadingGMaps = undefined;
            throw err;
        });
    }
    return __loadingGMaps;
};

export const metersToMiles = (meters: Meters): IMiles => {
    return isoMiles.wrap(isoMeters.unwrap(meters) / METERS_PER_MILE);
};

export const milesToMeters = (miles: IMiles): Meters => {
    return isoMeters.wrap(isoMiles.unwrap(miles) * METERS_PER_MILE);
};

export const kilometersToMeters = (km: IKilometers): Meters => {
    return isoMeters.wrap(isoKilometers.unwrap(km) * METERS_PER_KILOMETER);
};

export const metersToKilometers = (meters: Meters): IKilometers => {
    return isoKilometers.wrap(isoMeters.unwrap(meters) / METERS_PER_KILOMETER);
};

const EARTH_RADIUS_METERS = 6_371_008.8;

export const getGoogleLatLng = ({
    lat,
    lng,
}: {
    lat: Latitude;
    lng: Longitude;
}): google.maps.LatLngLiteral => {
    return {
        lat: parseFloat(isoLatitude.unwrap(lat)),
        lng: parseFloat(isoLongitude.unwrap(lng)),
    };
};

export const haversineDistanceMeters = (
    a: { lat: Latitude; lng: Longitude },
    b: { lat: Latitude; lng: Longitude },
): Meters => {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const pointA = getGoogleLatLng(a);
    const pointB = getGoogleLatLng(b);
    const lat1 = toRad(pointA.lat);
    const lat2 = toRad(pointB.lat);
    const dLat = toRad(pointB.lat - pointA.lat);
    const dLng = toRad(pointB.lng - pointA.lng);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
    return isoMeters.wrap(EARTH_RADIUS_METERS * c);
};

export const newMap = (
    mapDiv: HTMLElement,
    opts?: google.maps.MapOptions,
): google.maps.Map => {
    // Build the map
    const map = new google.maps.Map(mapDiv, opts);
    // Keep track of how many maps are on the page (since each call instantiation costs money).
    numMaps++;
    if (numMaps > 1) {
        console.trace(
            `Total of ${numMaps} have been loaded on this page. To reduce costs, aim for no more than 1 map load per page.`,
        );
    }
    // Return the map object to the caller
    return map;
};

export const newAutocomplete = <T extends HTMLInputElement>(
    inputElem: T,
    opts?: google.maps.places.AutocompleteOptions,
): google.maps.places.Autocomplete => {
    const autocomplete = new google.maps.places.Autocomplete(inputElem, {
        types: ["geocode"],
        componentRestrictions: {
            country: "us",
        },
        ...opts,
    });
    // Select all basic data fields: https://developers.google.com/maps/billing/gmp-billing#basic-data
    autocomplete.setFields([
        "address_component",
        "adr_address",
        "business_status",
        "formatted_address",
        "geometry",
        "icon",
        "name",
        "photo",
        "type",
        "url",
        "vicinity",
    ]);
    return autocomplete;
};

/**
 * Maps-readiness guards for components that may mount before the page's own
 * Maps `<script>` tag has finished loading (the full-document checkout renders
 * its loader async, so hydration can win that race). `ensurePlacesLib`
 * resolves the library in three tiers: adopt an already-loaded
 * `window.google`; await the page's own in-flight maps script; only when no
 * such script exists, load the API via `loadGMaps()`.
 */
const getLoadedPlacesLib = (): typeof google.maps | undefined => {
    if (
        typeof google !== "undefined" &&
        typeof google.maps !== "undefined" &&
        typeof google.maps.places !== "undefined"
    ) {
        return google.maps;
    }
    return undefined;
};

// Shared await on a maps <script> tag that is already on the page but still in
// flight, so concurrent callers dedupe onto one promise instead of each
// injecting a loadGMaps() copy of the API.
let __pendingScriptGMaps: Promise<typeof google.maps> | undefined;

const awaitPageMapsScript = (
    scriptElem: HTMLScriptElement,
): Promise<typeof google.maps> => {
    if (!__pendingScriptGMaps) {
        __pendingScriptGMaps = new Promise<typeof google.maps>(
            (resolve, reject) => {
                const controller = new AbortController();
                let intervalID: ReturnType<typeof setInterval> | undefined =
                    undefined;
                let timeoutID: ReturnType<typeof setTimeout> | undefined =
                    undefined;
                const cleanup = () => {
                    controller.abort();
                    if (intervalID !== undefined) {
                        clearInterval(intervalID);
                    }
                    if (timeoutID !== undefined) {
                        clearTimeout(timeoutID);
                    }
                };
                const finish = (): boolean => {
                    const maps = getLoadedPlacesLib();
                    if (maps) {
                        cleanup();
                        resolve(maps);
                        return true;
                    }
                    return false;
                };
                // The load event may have fired before we attached (or the
                // script may still be parsing); poll as a fallback.
                intervalID = setInterval(finish, 50);
                timeoutID = setTimeout(() => {
                    cleanup();
                    reject(
                        new Error(
                            "Timed out waiting for the page's Google Maps script",
                        ),
                    );
                }, 10_000);
                scriptElem.addEventListener(
                    "load",
                    () => {
                        // The Maps bootstrap may finish before its separately
                        // loaded Places library. Keep the polling fallback
                        // alive until the library arrives or the timeout fires.
                        finish();
                    },
                    { signal: controller.signal },
                );
                scriptElem.addEventListener(
                    "error",
                    () => {
                        cleanup();
                        reject(new Error("Could not load the Google Maps API"));
                    },
                    { signal: controller.signal },
                );
            },
        ).catch((err: unknown) => {
            // Reset the cached promise so subsequent calls can retry.
            __pendingScriptGMaps = undefined;
            throw err;
        });
    }
    return __pendingScriptGMaps;
};

export const ensurePlacesLib = (): Promise<typeof google.maps> => {
    const loaded = getLoadedPlacesLib();
    if (loaded) {
        return Promise.resolve(loaded);
    }
    // The page may carry its own maps <script> that is still in flight (the
    // full-document checkout renders its loader async): await THAT script
    // instead of injecting a second copy of the API. Only pages with no maps
    // script at all fall through to the loadGMaps() loader.
    const doc = getDocument();
    const scriptElem = doc?.querySelector<HTMLScriptElement>(
        'script[src*="maps.googleapis.com/maps/api/js"]',
    );
    if (scriptElem) {
        return awaitPageMapsScript(scriptElem);
    }
    return loadGMaps();
};

/**
 * Attach a places autocomplete to `inputElem` once the Maps library is ready,
 * invoking `onPlaceChanged` with the autocomplete on each selection. Returns a
 * disposer that both prevents a late attach (unmount before the library
 * resolves) and removes the listener -- suitable as a useEffect cleanup.
 */
export const wirePlacesAutocomplete = <T extends HTMLInputElement>(
    inputElem: T,
    onPlaceChanged: (autocomplete: google.maps.places.Autocomplete) => void,
    opts?: google.maps.places.AutocompleteOptions,
): (() => void) => {
    let disposed = false;
    let listener: google.maps.MapsEventListener | null = null;
    ensurePlacesLib()
        .then(() => {
            if (disposed) {
                return;
            }
            const autocomplete = newAutocomplete(inputElem, opts);
            listener = autocomplete.addListener("place_changed", () => {
                onPlaceChanged(autocomplete);
            });
        })
        .catch((e: unknown) => {
            console.warn("Google Maps failed to load", e);
        });
    return () => {
        disposed = true;
        if (
            listener &&
            "remove" in listener &&
            typeof listener.remove === "function"
        ) {
            listener.remove();
        }
    };
};

export const geocode = async (
    request: google.maps.GeocoderRequest,
): Promise<google.maps.GeocoderResult[]> => {
    await loadGMaps();
    const geocoder = new google.maps.Geocoder();
    return new Promise((resolve, reject) => {
        geocoder.geocode(request, (results, status) => {
            if (status !== google.maps.GeocoderStatus.OK || !results) {
                reject(new Error(status));
                return;
            }
            resolve(results);
        });
    });
};

export function roundCoordinatePrecision<T extends Latitude | Longitude>(
    coord: T | null | undefined,
    maxPrecision: number = COORD_MAX_DECIMALS,
): T | undefined {
    if (coord == null) {
        return undefined;
    }
    const tryRound = <U extends Latitude | Longitude>(iso: {
        unwrap: (v: U) => string;
        wrap: (v: string) => U;
    }): U | undefined => {
        try {
            const str = iso.unwrap(coord as unknown as U);
            const num = parseFloat(str);
            return !isNaN(num)
                ? iso.wrap(num.toFixed(maxPrecision))
                : undefined;
        } catch {
            return undefined;
        }
    };
    // Try as Latitude first, then Longitude
    const result =
        tryRound<Latitude>(isoLatitude) ?? tryRound<Longitude>(isoLongitude);
    return result as T | undefined;
}
